import { useMemo } from 'react';

import { formatDay, formatHm, formatInt, paintVar } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { PartyWrapped } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide12OutroProps = {
  readonly wrapped: PartyWrapped;
  readonly onReplay: () => void;
};

const CONFETTI_COUNT = 46;

/**
 * Deterministic rather than `Math.random`: the same party throws the same confetti on every
 * replay and on every screen in the room, and nothing here needs unpredictability.
 */
const pseudoRandom = (seed: number): number => {
  const wave = Math.sin(seed * 127.1 + 311.7) * 43_758.5453;

  return wave - Math.floor(wave);
};

type Piece = {
  readonly left: string;
  readonly width: string;
  readonly height: string;
  readonly durationS: string;
  readonly delayS: string;
  readonly spin: string;
};

const makePiece = (index: number): Piece => {
  const widthCqw = 1.4 + pseudoRandom(index * 4) * 2;

  return {
    left: `${String(Math.round(pseudoRandom(index * 4 + 1) * 100))}%`,
    width: `${widthCqw.toFixed(2)}cqw`,
    height: `${(widthCqw * 1.6).toFixed(2)}cqw`,
    durationS: `${(2.2 + pseudoRandom(index * 4 + 2) * 2).toFixed(2)}s`,
    delayS: `${(pseudoRandom(index * 4 + 3) * 1.4).toFixed(2)}s`,
    spin: `${String(Math.round((pseudoRandom(index * 4) - 0.5) * 1440))}deg`,
  };
};

export const Slide12Outro = ({ wrapped, onReplay }: Slide12OutroProps): ReactElement => {
  const pieces = useMemo(
    (): readonly Piece[] =>
      Array.from({ length: CONFETTI_COUNT }, (_unused, index) => makePiece(index)),
    [],
  );

  const stripe = wrapped.channels.slice(0, 3);
  const confettiPaints = [...stripe.map((channel) => channel.colour), paintVar('paper')];

  const facts = [
    { term: 'Ran for', value: formatHm(wrapped.durationMs), isName: false },
    { term: 'Tracks played', value: formatInt(wrapped.trackCount), isName: false },
    { term: 'Top artist', value: wrapped.topArtist?.name, isName: false },
    { term: 'Top track', value: wrapped.topTracks[0]?.name, isName: false },
    { term: 'Longest on the decks', value: wrapped.djs[0]?.name, isName: true },
    { term: 'Requests', value: formatInt(wrapped.crowd.wants), isName: false },
  ].flatMap((fact) =>
    fact.value === undefined ? [] : [{ term: fact.term, value: fact.value, isName: fact.isName }],
  );

  const cardStyle: StyleWithVars = { '--d': '150' };
  const buttonStyle: StyleWithVars = { '--d': '1200' };

  return (
    <>
      <div className="fizz" aria-hidden="true">
        {pieces.map((piece, index) => {
          const pieceStyle: StyleWithVars = {
            '--r': piece.spin,
            left: piece.left,
            width: piece.width,
            height: piece.height,
            background: confettiPaints[index % confettiPaints.length] ?? paintVar('paper'),
            animationDuration: piece.durationS,
            animationDelay: piece.delayS,
          };

          return (
            <i className="confetti" key={`${String(index)}-${piece.left}`} style={pieceStyle} />
          );
        })}
      </div>

      <div className="card pop" style={cardStyle}>
        <p className="card-name">{wrapped.party.displayName}</p>
        <p className="card-day">{formatDay(new Date(wrapped.party.createdAt))}</p>

        <dl>
          {facts.map((fact) => (
            <div className="fact" key={fact.term}>
              <dt>{fact.term}</dt>
              <dd className={fact.isName ? 'is-name' : undefined}>{fact.value}</dd>
            </div>
          ))}
        </dl>

        <div className="stripe" aria-hidden="true">
          {stripe.map((channel) => (
            <i key={channel.slotNumber} style={{ background: channel.colour }} />
          ))}
        </div>
      </div>

      <button type="button" className="again rise" style={buttonStyle} onClick={onReplay}>
        Watch again
      </button>
    </>
  );
};
